import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Database, 
  Server, 
  Cpu, 
  RefreshCcw, 
  ShieldCheck, 
  AlertTriangle,
  Clock,
  Globe
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { supabase } from '../../lib/supabase';

const monitorData = [
  { time: '12:00', latency: 45, load: 24 },
  { time: '12:05', latency: 52, load: 32 },
  { time: '12:10', latency: 48, load: 28 },
  { time: '12:15', latency: 61, load: 45 },
  { time: '12:20', latency: 55, load: 38 },
  { time: '12:25', latency: 42, load: 25 },
  { time: '12:30', latency: 40, load: 22 },
];

const HealthCard = ({ title, status, icon: Icon, color, detail }) => (
  <div className="admin-card p-6 rounded-3xl">
    <div className="flex items-center justify-between mb-4">
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center"
        style={{ background: `${color}22`, border: `1px solid ${color}44` }}
      >
        <Icon size={24} style={{ color }} />
      </div>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${status === 'Healthy' ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
        <span className={`text-xs font-bold ${status === 'Healthy' ? 'text-emerald-400' : 'text-rose-400'}`}>{status}</span>
      </div>
    </div>
    <h3 className="text-lg font-bold text-white mb-1">{title}</h3>
    <p className="text-xs text-white/40">{detail}</p>
  </div>
);

const SystemMonitor = () => {
  const [supabaseStatus, setSupabaseStatus] = useState('Checking...');
  const [lastCheck, setLastCheck] = useState(new Date().toLocaleTimeString());

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const { error } = await supabase.from('profiles').select('count', { count: 'exact', head: true });
        if (error) throw error;
        setSupabaseStatus('Healthy');
      } catch (err) {
        setSupabaseStatus('Disconnected');
      }
      setLastCheck(new Date().toLocaleTimeString());
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text flex items-center gap-3">
            <Activity className="text-violet-400" size={32} />
            Backend Monitor
          </h1>
          <p className="text-white/40 mt-1 text-sm">Real-time status of your Convex infrastructure.</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400 font-mono">LAST SCAN: {lastCheck}</p>
          <div className="flex items-center gap-2 justify-end mt-1">
            <RefreshCcw size={14} className="text-indigo-500 animate-spin-slow" />
            <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest">Live Monitoring</span>
          </div>
        </div>
      </div>

      {/* Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <HealthCard title="Database"     status={supabaseStatus} icon={Database}    color="#a78bfa" detail="Convex • Serverless Cloud" />
        <HealthCard title="Auth Service"  status={supabaseStatus} icon={ShieldCheck} color="#10b981" detail="Convex Auth • Session Tokens" />
        <HealthCard title="Realtime"      status="Healthy"        icon={RefreshCcw}  color="#f59e0b" detail="Convex Live Queries Active" />
        <HealthCard title="File Storage"  status="Healthy"        icon={Server}      color="#a78bfa" detail="Convex Storage • Auto-scaling" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Latency Chart */}
        <div className="lg:col-span-2 admin-card p-6 rounded-3xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Clock className="text-indigo-500" size={20} />
              <h3 className="text-lg font-bold dark:text-white">API Latency (ms)</h3>
            </div>
            <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-[10px] font-bold text-slate-500">REAL-TIME</span>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monitorData}>
                <defs>
                  <linearGradient id="colorLatency" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-800" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10}} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', borderRadius: '12px', border: 'none', color: '#fff' }}
                  itemStyle={{ color: '#fff' }}
                />
                <Area type="monotone" dataKey="latency" stroke="#6366f1" strokeWidth={3} fill="url(#colorLatency)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Server Load */}
        <div className="admin-card p-6 rounded-3xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Cpu className="text-emerald-500" size={20} />
              <h3 className="text-lg font-bold dark:text-white">Server Load</h3>
            </div>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">CPU Usage</span>
                <span className="font-bold dark:text-white">12%</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '12%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">Memory</span>
                <span className="font-bold dark:text-white">42%</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: '42%' }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">Storage (Media)</span>
                <span className="font-bold dark:text-white">8%</span>
              </div>
              <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: '8%' }} />
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800">
            <div className="flex items-center gap-3 text-indigo-600 dark:text-indigo-400">
              <Globe size={18} />
              <span className="text-xs font-bold uppercase tracking-wider">Origin: Global Edge</span>
            </div>
            <p className="text-[10px] text-indigo-500/70 mt-1">Traffic is being routed through the nearest available data center.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
